import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";

/**
 * File type definitions
 */
export const FILE_TYPES = {
    IMAGE: ["image/jpeg", "image/png", "image/webp", "image/jpg"],
    PDF: ["application/pdf"],
    PPT: [
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
    ALL: [
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/jpg",
        "application/pdf",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
};

/**
 * Extension mapping (STRICT)
 */
const EXTENSION_MAP = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/jpg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
    "application/pdf": [".pdf"],
    "application/vnd.ms-powerpoint": [".ppt"],
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
};

/**
 * Limits
 */
const IMAGE_MAX_MB = 1;
const IMAGE_MAX_ORIGINAL_MB = 10;
const DOC_MAX_SIZE_MB = 20;

/**
 * Validate file type (STRICT BASED ON allowedTypes)
 */
const validateFileType = (file, allowedTypes) => {
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    if (!allowedTypes.includes(fileType)) {
        toast.error("Invalid file type");
        return false;
    }

    const allowedExtensions = allowedTypes.flatMap(
        (type) => EXTENSION_MAP[type] || []
    );

    const isValidExtension = allowedExtensions.some((ext) =>
        fileName.endsWith(ext)
    );

    if (!isValidExtension) {
        toast.error("File extension does not match file type");
        return false;
    }

    return true;
};

/**
 * Validate file size
 */
const validateFileSize = (file) => {
    const sizeMB = file.size / (1024 * 1024);

    if (file.type.startsWith("image/")) {
        if (sizeMB > IMAGE_MAX_ORIGINAL_MB) {
            toast.error("Image too large (max 10MB)");
            return false;
        }
    } else {
        if (sizeMB > DOC_MAX_SIZE_MB) {
            toast.error("File size must be less than 20MB");
            return false;
        }
    }

    return true;
};

/**
 * Compress image under 1MB
 */
const compressImage = async (file) => {
    try {
        let compressedFile = file;
        let attempts = 0;

        while (
            compressedFile.size / (1024 * 1024) > IMAGE_MAX_MB &&
            attempts < 8
        ) {
            const options = {
                maxSizeMB: IMAGE_MAX_MB,
                maxWidthOrHeight: 1280 - attempts * 100,
                useWebWorker: true,
                initialQuality: Math.max(0.8 - attempts * 0.1, 0.2),
            };

            compressedFile = await imageCompression(compressedFile, options);
            attempts++;
        }

        return new File([compressedFile], file.name, {
            type: compressedFile.type,
        });

    } catch (error) {
        console.error("Compression error:", error);
        return file;
    }
};

/**
 * Main handler
 */
export const processFileUpload = async (file, config = {}) => {
    const { allowedTypes = FILE_TYPES.ALL } = config;

    if (!file) return null;

    if (!validateFileType(file, allowedTypes)) return null;

    if (!validateFileSize(file)) return null;

    if (file.type.startsWith("image/")) {
        return await compressImage(file);
    }

    return file;
};